import {
  ColumnDef,
  ColumnFiltersState,
  ExpandedState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  PaginationState,
  RowSelectionState,
  SortingState,
  TableState,
  Updater,
  useReactTable,
} from '@tanstack/react-table'
import classnames from 'classnames'
import { debounce } from 'lodash'
import {
  Dispatch,
  forwardRef,
  Ref,
  SetStateAction,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react'

import { Table, TableCell, TableHeading } from '@nike/eds'

import {
  ColumnFilters,
  exactStringFilter,
  GlobalFilter,
  PaginationControls,
  SortableHeader,
} from './table-components'
import { includesStringFilter } from './table-components/ColumnFilters'
import styles from './Table.module.styl'
import './table.scss'

type ExtendedColumnDef<T> = ColumnDef<T, any> & {
  filterType?: 'text' | 'select'
  filterOptions?: Array<{ value: string; label: string }>
}

type TableProps<T> = {
  columns: ExtendedColumnDef<T>[]
  data: T[]
  // sorting
  enableSorting?: boolean

  // sticky elements
  stickyColumnIndex?: number
  disableStickyHeader?: boolean

  // expanded
  enableExpanding?: boolean
  initialExpanded?: ExpandedState

  // pagination
  enablePagination?: boolean
  pageSize?: number
  pageSizeOptions?: number[]

  // filtering
  enableGlobalFilter?: boolean
  enableColumnFilters?: boolean

  // options
  noDataText?: string

  // on change events
  onPaginationChange?: (pagination: PaginationState) => void
  onColumnFiltersChange?: (columnFilters: ColumnFiltersState) => void
  onGlobalFilterChange?: (globalFilter: string) => void
  onSortChange?: (sorting: SortingState) => void
  onRowSelectionChange?: (rowSelection: RowSelectionState) => void

  // other react-table props
  [key: string]: any
}

export type TableRef = {
  getTableState: () => TableState
  resetPageIndex: () => void
  setPagination: Dispatch<SetStateAction<PaginationState>>
  setColumnFilters: Dispatch<SetStateAction<ColumnFiltersState>>
  setSorting: Dispatch<SetStateAction<SortingState>>
  setExpanded: Dispatch<SetStateAction<ExpandedState>>
}

const hasChildren = <T,>(row: any): row is { children: T[] } => {
  return !!row.children?.length
}

export default forwardRef(function DataTable<T>(
  {
    columns,
    data,
    enableSorting = false,
    enablePagination = false,
    enableGlobalFilter = false,
    enableColumnFilters = false,
    enableExpanding = false,
    disableStickyHeader = false,
    initialColumnFilters = [],
    initialSort = [],
    initialExpanded = {},
    pageIndex = 0,
    pageSize = 10,
    pageSizeOptions = [10, 25, 50],
    autoResetPageIndex = false,
    noDataText = 'No items found.',
    stickyColumnIndex,
    onColumnFiltersChange,
    onGlobalFilterChange,
    onPaginationChange,
    onSortChange,
    onRowSelectionChange,
    ...rest
  }: TableProps<T>,
  ref: Ref<TableRef>
) {
  const [globalFilter, setGlobalFilter] = useState('')
  const [sorting, setSorting] = useState<SortingState>(initialSort)
  const [expanded, setExpanded] = useState<ExpandedState>(initialExpanded)
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex,
    pageSize,
  })
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(initialColumnFilters)
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})

  const debouncedSetGlobalFilter = useMemo(
    () =>
      debounce((updater: Updater<string>) => {
        setGlobalFilter((prev: string) => {
          const next = updater instanceof Function ? updater(prev) : updater
          onGlobalFilterChange?.(next)
          return next
        })
      }, 100),
    []
  )

  const handleGlobalFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    debouncedSetGlobalFilter(e.target.value)
  }

  const handleSortingChange = (updater: Updater<SortingState>) => {
    setSorting((prev: SortingState) => {
      const next = updater instanceof Function ? updater(prev) : updater
      onSortChange?.(next)
      return next
    })
  }

  const handlePaginationChange = (updater: Updater<PaginationState>) => {
    setPagination((prev: PaginationState) => {
      const next = updater instanceof Function ? updater(prev) : updater
      onPaginationChange?.(next)
      return next
    })
  }

  const handleColumnFiltersChange = (updater: Updater<ColumnFiltersState>) => {
    if (!autoResetPageIndex) {
      setPagination({
        ...pagination,
        pageIndex: 0,
      })
    }
    setColumnFilters((prev: ColumnFiltersState) => {
      const next = updater instanceof Function ? updater(prev) : updater
      onColumnFiltersChange?.(next)
      return next
    })
  }

  const handleRowSelectionChange = (updater: Updater<RowSelectionState>) => {
    setRowSelection((prev: RowSelectionState) => {
      const next = updater instanceof Function ? updater(prev) : updater
      onRowSelectionChange?.(next)
      return next
    })
  }

  const processedColumns = useMemo(() => {
    return columns.map((column) => {
      if (column.filterType === 'select') {
        return {
          ...column,
          filterFn: column.filterFn || exactStringFilter,
        }
      }

      return {
        ...column,
        filterFn: column.filterFn || includesStringFilter,
      }
    })
  }, [columns])

  const table = useReactTable({
    data,
    columns: processedColumns,
    onSortingChange: handleSortingChange,
    onPaginationChange: handlePaginationChange,
    onColumnFiltersChange: handleColumnFiltersChange,
    onExpandedChange: setExpanded,
    onRowSelectionChange: handleRowSelectionChange,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: enableSorting ? getSortedRowModel() : undefined,
    getPaginationRowModel: enablePagination ? getPaginationRowModel() : undefined,
    getFilteredRowModel:
      enableGlobalFilter || enableColumnFilters ? getFilteredRowModel() : undefined,
    getExpandedRowModel: getExpandedRowModel(),
    getSubRows: (row: T) => {
      return hasChildren<T>(row) ? row.children : undefined
    },
    autoResetPageIndex,
    ...rest,
    state: {
      globalFilter: enableGlobalFilter ? globalFilter : undefined,
      sorting: enableSorting ? sorting : [],
      pagination: enablePagination ? pagination : undefined,
      columnFilters: enableColumnFilters ? columnFilters : [],
      expanded: enableExpanding ? expanded : {},
      rowSelection: rowSelection || {},
      ...rest.state,
    },
  })
  // expose table state getter and reset PageIndex for consumers
  useImperativeHandle(ref, () => ({
    getTableState: () => table.getState(),
    resetPageIndex: () =>
      setPagination({
        ...pagination,
        pageIndex: 0,
      }),
    setPagination,
    setColumnFilters,
    setSorting,
    setExpanded,
  }))

  const rows = table.getRowModel().rows

  return (
    <div className="tableWrapper">
      {enableGlobalFilter && (
        <GlobalFilter
          globalFilter={globalFilter}
          handleGlobalFilterChange={handleGlobalFilterChange}
        />
      )}
      <div
        className={classnames('tableContainer ', 'overflowAuto')}
      >
        <Table isFullWidth>
          <thead className={styles.tableHeader}>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header, index) => (
                  <TableHeading
                    className={classnames(`table--header__${header.id}`, {
                      [styles.stickyColumnHeader]: index === stickyColumnIndex,
                    })}
                    style={{
                      width: header.getSize(),
                    }}
                    key={`${header.id}.${header.column.columnDef.header}`}
                  >
                    <div className={styles.headerCellLabel}>
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {enableSorting && (
                        <SortableHeader
                          getCanSort={header.column.getCanSort}
                          // @ts-ignore
                          getToggleSortingHandler={header.column.getToggleSortingHandler}
                          getIsSorted={header.column.getIsSorted}
                        />
                      )}
                    </div>

                    {enableColumnFilters && header.column.getCanFilter() ? (
                      <ColumnFilters
                        enableColumnFilters={enableColumnFilters}
                        header={header as any}
                      />
                    ) : null}
                  </TableHeading>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {rows.length > 0 ? (
              rows.map((row) => {
                return (
                  <tr
                    key={row.id}
                    className={row.getIsSelected() ? 'selected' : undefined}
                    onClick={e => {
                      if (!row.getIsSelected()) {
                        row.getToggleSelectedHandler()(e);
                      }
                      // else do nothing if already selected
                    }}
                  >
                    {row.getVisibleCells().map((cell, index) => (
                      <TableCell
                        className={classnames({
                          [styles.stickyColumnBody]: index === stickyColumnIndex,
                          [styles.expandableRowCell]: row.getCanExpand(),
                        })}
                        key={`${cell.id}.${cell.column.columnDef.header}`}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </tr>
                )
              })
            ) : (
              <tr>
                <TableCell colSpan={table.getAllColumns().length}>
                  <div className={styles.noDataText}>
                    {noDataText} {!!columnFilters.length && 'Try adjusting your filters.'}
                  </div>
                </TableCell>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
      {enablePagination && <PaginationControls table={table} pageSizeOptions={pageSizeOptions} />}
    </div>
  )
})
